/**
 * API client abstraction with error handling
 * @module api
 */

/** Default request timeout in milliseconds */
const DEFAULT_TIMEOUT = 15000;

/**
 * Custom error class for API failures
 */
export class ApiError extends Error {
  /**
   * @param {number} status - HTTP status code (0 for network errors)
   * @param {string} message - Error description
   * @param {*} [data] - Optional response data
   */
  constructor(status, message, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;

    // Network errors, rate limiting, and server errors are retryable
    this.isRetryable = status === 0 || status === 429 || status >= 500;

    // Authentication failures require user action
    this.isAuthError = status === 401;
  }
}

/**
 * API client for server communication
 */
class ApiClient {
  /**
   * @param {string} [baseUrl=''] - Base URL prefix for all requests
   */
  constructor(baseUrl = '') {
    this.baseUrl = baseUrl;
  }

  /**
   * Execute an HTTP request
   * @param {string} method - HTTP method
   * @param {string} path - Request path
   * @param {Object} [options={}] - Request options
   * @param {Object} [options.params] - Query parameters
   * @param {Object} [options.body] - Request body (will be JSON-serialized)
   * @param {number} [options.timeout] - Request timeout in ms
   * @returns {Promise<*>} Parsed response data
   * @throws {ApiError} On request failure
   */
  async request(method, path, options = {}) {
    const { params, body, timeout = DEFAULT_TIMEOUT } = options;

    // Construct URL with query parameters
    let url = `${this.baseUrl}${path}`;
    if (params) {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      }
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // Configure abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Build fetch options
    const fetchOptions = {
      method,
      credentials: 'same-origin',
      signal: controller.signal,
      headers: {}
    };

    if (body !== undefined) {
      fetchOptions.headers['Content-Type'] = 'application/json';
      fetchOptions.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      // Parse response body
      let data = null;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      }

      // Handle error responses
      if (!response.ok) {
        const message = data?.error || data?.message || response.statusText || 'Request failed';
        throw new ApiError(response.status, message, data);
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      // Re-throw ApiError instances as-is
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle abort/timeout
      if (error.name === 'AbortError') {
        throw new ApiError(0, 'Request timed out');
      }

      // Network or other errors
      throw new ApiError(0, error.message || 'Network error');
    }
  }

  /**
   * GET request
   * @param {string} path - Request path
   * @param {Object} [params] - Query parameters
   * @returns {Promise<*>}
   */
  get(path, params) {
    return this.request('GET', path, { params });
  }

  /**
   * POST request
   * @param {string} path - Request path
   * @param {Object} [body] - Request body
   * @returns {Promise<*>}
   */
  post(path, body) {
    return this.request('POST', path, { body });
  }

  /**
   * PUT request
   * @param {string} path - Request path
   * @param {Object} [body] - Request body
   * @returns {Promise<*>}
   */
  put(path, body) {
    return this.request('PUT', path, { body });
  }

  /**
   * DELETE request
   * @param {string} path - Request path
   * @param {Object} [params] - Query parameters
   * @returns {Promise<*>}
   */
  delete(path, params) {
    return this.request('DELETE', path, { params });
  }
}

/** Singleton API client instance */
export const api = new ApiClient();
